import { SCROLL_CONFIG, UI_DELAYS, VALIDATION } from "./provetConstants";

/**
 * Escapes a CSS selector to make it valid for querySelector.
 * @param selector - The selector to escape
 * @returns Escaped selector string
 */
const escapeSelector = (selector: string): string => {
  // If selector starts with #, escape the ID part
  if (selector.startsWith("#")) {
    const id = selector.substring(1);
    // Use CSS.escape if available, otherwise manual escaping
    if (typeof CSS !== "undefined" && CSS.escape) {
      return "#" + CSS.escape(id);
    } else {
      // Manual escaping for common cases
      // Escape leading digits and special characters
      return "#" + id.replace(/^(\d)/, "\\3$1 ").replace(/([^a-zA-Z0-9_-])/g, "\\$1");
    }
  }
  return selector;
};

/**
 * Scrolls to the first validation error element on the page.
 * @param errorSelector - CSS selector for validation error elements (default from constants)
 * @param offset - Offset from the top in pixels (default from constants)
 * @param behavior - Scroll behavior (default from constants)
 * @returns Promise that resolves when scrolling is complete
 */
export const scrollToValidationError = async (
  errorSelector: string = VALIDATION.ERROR_SELECTOR,
  offset: number = SCROLL_CONFIG.DEFAULT_OFFSET,
  behavior: ScrollBehavior = SCROLL_CONFIG.DEFAULT_BEHAVIOR
): Promise<void> => {
  // Wait a small amount to ensure DOM is updated
  await new Promise(resolve => setTimeout(resolve, UI_DELAYS.DOM_UPDATE));

  // Find the first validation error element
  const errorElement = document.querySelector(escapeSelector(errorSelector));

  if (!errorElement) {
    console.warn(`No validation error found with selector: ${errorSelector}`);
    return;
  }

  // Use scrollToElement to handle the actual scrolling
  await scrollToElement(errorSelector, offset, behavior);

  // Try to focus the first focusable element within the error container
  const focusableElement = errorElement.querySelector(
    'input, textarea, select, button, [tabindex]:not([tabindex="-1"])'
  ) as HTMLElement;

  if (focusableElement) {
    // Small delay to ensure scroll completes before focusing
    setTimeout(
      () => {
        focusableElement.focus();
      },
      behavior === "smooth" ? UI_DELAYS.FOCUS_AFTER_SCROLL : 0
    );
  }
};

/**
 * Scrolls to a specific element by ID or selector.
 * @param elementSelector - Element ID (with #) or CSS selector
 * @param offset - Offset from the top in pixels (default from constants)
 * @param behavior - Scroll behavior (default from constants)
 * @returns Promise that resolves when scrolling is complete
 */
export const scrollToElement = async (
  elementSelector: string,
  offset: number = SCROLL_CONFIG.DEFAULT_OFFSET,
  behavior: ScrollBehavior = SCROLL_CONFIG.DEFAULT_BEHAVIOR
): Promise<void> => {
  // Wait a small amount to ensure DOM is updated
  await new Promise(resolve => setTimeout(resolve, UI_DELAYS.DOM_UPDATE));

  const element = document.querySelector(escapeSelector(elementSelector));

  if (!element) {
    console.warn(`Element not found with selector: ${elementSelector}`);
    return;
  }

  scrollElementToTop(element, offset, behavior);
};

/**
 * Scrolls the page so that an element sits just below the top of the viewport.
 * @param element - The element to scroll to
 * @param offset - Offset from the top in pixels, e.g. for the sticky header (default from constants)
 * @param behavior - Scroll behavior (default from constants)
 */
export const scrollElementToTop = (
  element: Element,
  offset: number = SCROLL_CONFIG.DEFAULT_OFFSET,
  behavior: ScrollBehavior = SCROLL_CONFIG.DEFAULT_BEHAVIOR
): void => {
  // Get the element's position relative to the document
  const elementRect = element.getBoundingClientRect();
  const elementTop = elementRect.top + window.pageYOffset;

  // Calculate the scroll position with offset
  const scrollToPosition = Math.max(0, elementTop - offset);

  // Scroll to the calculated position
  window.scrollTo({
    top: scrollToPosition,
    behavior: behavior
  });
};

/**
 * Scrolls to the first invalid form field.
 * @param formSelector - CSS selector for the form container (default: 'form')
 * @param offset - Offset from the top in pixels (default from constants)
 * @param behavior - Scroll behavior (default from constants)
 * @returns Promise that resolves when scrolling is complete
 */
export const scrollToFirstInvalidField = async (
  formSelector: string = "form",
  offset: number = SCROLL_CONFIG.DEFAULT_OFFSET,
  behavior: ScrollBehavior = SCROLL_CONFIG.DEFAULT_BEHAVIOR
): Promise<void> => {
  // Wait a small amount to ensure DOM is updated
  await new Promise(resolve => setTimeout(resolve, UI_DELAYS.DOM_UPDATE));

  const form = document.querySelector(escapeSelector(formSelector));

  if (!form) {
    console.warn(`Form not found with selector: ${formSelector}`);
    return;
  }

  // Find the first invalid field
  const invalidField = form.querySelector(
    'input:invalid, textarea:invalid, select:invalid, [aria-invalid="true"]'
  ) as HTMLElement;

  if (!invalidField) {
    // Fallback to validation error selector
    await scrollToValidationError(VALIDATION.ERROR_SELECTOR, offset, behavior);
    return;
  }

  // Get selector for the invalid field (prefer ID, fallback to generating a unique selector)
  const fieldSelector = invalidField.id
    ? `#${invalidField.id}`
    : escapeSelector(formSelector) +
      " " +
      invalidField.tagName.toLowerCase() +
      ":invalid";

  // Use scrollToElement to handle the actual scrolling
  await scrollToElement(fieldSelector, offset, behavior);

  // Focus the invalid field
  setTimeout(
    () => {
      invalidField.focus();
    },
    behavior === "smooth" ? UI_DELAYS.FOCUS_AFTER_SCROLL : 0
  );
};

/**
 * Scrolls to dialog steps (equivalent to ProvetJsInteropService.ScrollToDialog).
 * @param elementId - The element ID to scroll to
 * @param offset - Offset from the top in pixels (default: dialog offset)
 * @param behavior - Scroll behavior (default from constants)
 * @returns Promise that resolves when scrolling is complete
 */
export const scrollToDialog = async (
  elementId: string,
  offset: number = SCROLL_CONFIG.DIALOG_OFFSET,
  behavior: ScrollBehavior = SCROLL_CONFIG.DEFAULT_BEHAVIOR
): Promise<void> => {
  // Try multiple times with increasing delays if element not found
  // This handles cases where React hasn't finished rendering yet
  const maxAttempts = SCROLL_CONFIG.MAX_FIND_ATTEMPTS;
  const baseDelay = UI_DELAYS.ELEMENT_FIND_RETRY;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Create a properly escaped selector
    const selector = `#${elementId}`;
    const escapedSelector = escapeSelector(selector);
    const element = document.querySelector(escapedSelector);

    if (element) {
      // Element found, proceed with scroll
      await scrollToElement(selector, offset, behavior);
      return;
    }

    // Element not found, wait before next attempt
    if (attempt < maxAttempts - 1) {
      await new Promise(resolve => setTimeout(resolve, baseDelay * (attempt + 1)));
    }
  }

  // If we get here, element was never found
  console.warn(
    `Could not find element with ID: ${elementId} after ${maxAttempts} attempts`
  );

  // Log all available elements with similar IDs for debugging
  const partialId = elementId.split("_")[0];
  const allElements = document.querySelectorAll(
    `[id*="${CSS.escape ? CSS.escape(partialId) : partialId}"]`
  );
  console.log(
    "Available elements with partial ID match:",
    Array.from(allElements).map(el => ({ id: el.id, tagName: el.tagName }))
  );
};

/**
 * General element scrolling (equivalent to BrowserJsInteropService.ScrollIntoView).
 * @param element - The HTML element to scroll to
 * @param block - Defines vertical alignment (default: 'start')
 * @param behavior - Scroll behavior (default from constants)
 */
export const scrollIntoView = (
  element: HTMLElement,
  block: ScrollLogicalPosition = "start",
  behavior: ScrollBehavior = SCROLL_CONFIG.DEFAULT_BEHAVIOR
): void => {
  if (element) {
    element.scrollIntoView({ behavior, block });
  }
};
